import type { TextFieldProps } from '@mui/material'
import {
  getFieldSurfaceDefaults,
  getFieldSurfaceStyle,
  shouldUseLabelAsPlaceholder,
} from './fieldSurfaces/registry'
import type { FieldSurface } from './fieldSurfaces/types'
import { isAboveLabelLayout, shouldShrinkOutlineLabel } from './formFieldTokens'
import { getFormFieldTextFieldSx } from './formFieldStyles'
import type { BaseFormFieldProps } from './formFieldTypes'

export type BuildTextFieldPropsInput = BaseFormFieldProps &
  Pick<TextFieldProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'inputRef' | 'type' | 'multiline' | 'rows' | 'minRows' | 'maxRows' | 'slotProps' | 'InputProps'> & {
    autoComplete?: string
    surface?: FieldSurface
  }

export function buildTextFieldProps({
  label,
  placeholder,
  required,
  disabled,
  error,
  helperText,
  size,
  fullWidth,
  sx,
  id,
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  inputRef,
  type,
  multiline,
  rows,
  minRows,
  maxRows,
  slotProps,
  InputProps,
  autoComplete,
  surface = 'form',
}: BuildTextFieldPropsInput): TextFieldProps {
  const defaults = getFieldSurfaceDefaults(surface)
  const style = getFieldSurfaceStyle(surface)
  const resolvedSize = size ?? defaults.size
  const resolvedFullWidth = fullWidth ?? defaults.fullWidth
  const above = isAboveLabelLayout(surface)
  const outlineShrink = shouldShrinkOutlineLabel(surface)
  const labelAsPlaceholder = shouldUseLabelAsPlaceholder(surface)
  const resolvedPlaceholder = labelAsPlaceholder ? (placeholder ?? label) : placeholder
  const fieldLabel = labelAsPlaceholder ? undefined : label

  /**
   * MUI v6: luôn set `slotProps.input` (font sx) → nếu không merge `InputProps`
   * thì mất endAdornment. Bỏ `ref` khi spread để tránh lệch LegacyRef.
   */
  const {
    ref: ignoredInputRef,
    sx: inputPropsSx,
    ...inputPropsRest
  } = (InputProps ?? {}) as { ref?: unknown; sx?: object } & Record<string, unknown>
  void ignoredInputRef
  const slotInputRaw =
    slotProps?.input && typeof slotProps.input === 'object' ? slotProps.input : undefined
  const { sx: slotInputSx, ...slotInputRest } =
    (slotInputRaw && typeof slotInputRaw === 'object' && !('$$typeof' in slotInputRaw)
      ? slotInputRaw
      : {}) as { sx?: object } & Record<string, unknown>

  return {
    id,
    name,
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    inputRef,
    type,
    multiline,
    rows,
    minRows,
    maxRows,
    autoComplete,
    fullWidth: resolvedFullWidth,
    size: resolvedSize,
    variant: defaults.variant,
    hiddenLabel: above || labelAsPlaceholder,
    label: above || labelAsPlaceholder ? undefined : fieldLabel,
    InputLabelProps:
      above || labelAsPlaceholder || !fieldLabel
        ? undefined
        : { shrink: outlineShrink ? true : undefined },
    placeholder: resolvedPlaceholder,
    required,
    disabled,
    error,
    helperText: above ? undefined : helperText,
    sx: getFormFieldTextFieldSx(surface, sx),
    slotProps: {
      ...slotProps,
      input: {
        ...inputPropsRest,
        ...slotInputRest,
        sx: {
          fontFamily: style.fontFamily,
          fontSize: `${style.inputFontSizePx}px`,
          fontWeight: style.inputFontWeight,
          ...(typeof inputPropsSx === 'object' && inputPropsSx ? inputPropsSx : {}),
          ...(typeof slotInputSx === 'object' && slotInputSx ? slotInputSx : {}),
        },
      } as NonNullable<TextFieldProps['slotProps']>['input'],
    },
  }
}
